import React from "react";
import {
	Container,
	Header,
	Title,
	BadgeCount,
	EmptyStateContainer,
	EmptyTitle,
	EmptyDesc,
	TaskListContainer,
	TaskCard,
	TaskLeft,
	TaskInfo,
	TaskPatientName,
	TaskDesc,
	TaskRight,
	TaskActionButton,
	TrashButton,
	TaskError,
	FocusError,
	SkeletonCard,
	RefreshingText,
} from "./styles";
import { ReactComponent as DescriptionIcon } from "../../../../assets/kinologyIcons/svg/description_dash.svg";
import { ReactComponent as ClearIcon } from "../../../../assets/kinologyIcons/svg/clear_dash.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/kinologyIcons/svg/trash.svg";
import { useTodayFocusViewModel } from "./useTodayFocusViewModel";
import {
	TodayFocusItem,
	TodayFocusQueueKey,
} from "../../../../store/ducks/operationalDashboard";

interface TodayFocusProps {
	onOpenContact: (queueKey: TodayFocusQueueKey, item: TodayFocusItem) => void;
}

const TodayFocus: React.FC<TodayFocusProps> = ({ onOpenContact }) => {
	const {
		cards,
		isInitialLoading,
		isRefreshing,
		isLoaded,
		error,
		retry,
		handlePrimaryAction,
		handleDismiss,
	} = useTodayFocusViewModel(onOpenContact);

	return (
		<Container aria-labelledby="today-focus-title">
			<Header>
				<Title id="today-focus-title">
					<DescriptionIcon width="16px" height="16px" fill="#000e4b" />
					Foco de Hoje
				</Title>
				{isLoaded && (
					<BadgeCount>
						{cards.length} {cards.length === 1 ? "Tarefa" : "Tarefas"}
					</BadgeCount>
				)}
			</Header>

			{isInitialLoading ? (
				<TaskListContainer aria-label="Carregando Foco de Hoje">
					<SkeletonCard />
					<SkeletonCard />
					<SkeletonCard />
				</TaskListContainer>
			) : error && cards.length === 0 ? (
				<FocusError role="alert">
					<span>Não foi possível carregar o Foco de Hoje.</span>
					<button type="button" onClick={retry}>
						Tentar novamente
					</button>
				</FocusError>
			) : cards.length > 0 ? (
				<>
					{isRefreshing && (
						<RefreshingText>Atualizando tarefas...</RefreshingText>
					)}
					{error && (
						<FocusError role="alert">
							<span>Não foi possível atualizar as tarefas.</span>
							<button type="button" onClick={retry}>
								Tentar novamente
							</button>
						</FocusError>
					)}
					<TaskListContainer>
						{cards.map((card) => (
							<TaskCard
								key={`${card.item.FOCO_TIPO}:${card.item.ID_USUARIO}:${card.item.ID_EXECUCAO_REFERENCIA}`}
								aria-busy={card.loading}
								completion={card.completion}
							>
								<TaskLeft>
									<TaskInfo>
										<TaskPatientName>{card.name}</TaskPatientName>
										<TaskDesc>{card.description}</TaskDesc>
										{card.error && (
											<TaskError role="alert">{card.error}</TaskError>
										)}
									</TaskInfo>
								</TaskLeft>
								<TaskRight>
									<TaskActionButton
										type="button"
										onClick={() => handlePrimaryAction(card)}
										disabled={card.loading}
									>
										{card.completion === "success"
											? "Concluído"
											: card.loading
											? "Processando..."
											: card.cta}
									</TaskActionButton>
									<TrashButton
										type="button"
										onClick={() => handleDismiss(card)}
										disabled={card.loading || !!card.completion}
										aria-label={`Descartar tarefa de ${card.name}`}
									>
										<TrashIcon width="18px" height="18px" />
									</TrashButton>
								</TaskRight>
							</TaskCard>
						))}
					</TaskListContainer>
				</>
			) : isLoaded ? (
				<EmptyStateContainer>
					<ClearIcon width="56px" height="56px" />
					<EmptyTitle>Caixa de entrada limpa!</EmptyTitle>
					<EmptyDesc>
						Muito bem, você completou todas as suas tarefas!
					</EmptyDesc>
				</EmptyStateContainer>
			) : null}
		</Container>
	);
};

export default TodayFocus;
